import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  ListItemButton,
  DialogContentText
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { Budget, BudgetCategory } from '../../types';
import { budgets } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useTransactions } from '../../contexts/TransactionContext';

interface BudgetCardProps {
  budget: Budget;
  onUpdate: (updatedFields: Partial<Budget>) => void;
  onDelete?: () => void;
}

interface EditingCategory {
  index: number;
  name: string;
  budgetedAmount: string;
  type: 'income' | 'expense' | 'savings';
}

interface NewCategory {
  name: string;
  budgetedAmount: string;
  type: 'income' | 'expense' | 'savings';
}

const BudgetCard: React.FC<BudgetCardProps> = ({ budget, onUpdate, onDelete }) => {  
  const { showNotification } = useNotification();
  const { refreshTransactions } = useTransactions();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<EditingCategory | null>(null);
  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState<NewCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<{
    name: string;
    type: 'income' | 'expense' | 'savings';
    actualAmount: number;
  } | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculateProgress = (actual: number, budgeted: number, type?: 'income' | 'expense' | 'savings') => {
    if (budgeted === 0) return 0;
    
    // For savings categories, invert the progress logic
    if (type === 'savings') {
      // Savings progress: negative when under goal, positive when over goal
      const savingsProgress = ((actual - budgeted) / budgeted) * 100;
      return savingsProgress;
    }
    
    // For expense categories, use normal progress calculation
    return (actual / budgeted) * 100;
  };

  const getProgressColor = (actual: number, budgeted: number) => {
    const percentage = (actual / budgeted) * 100;
    if (percentage > 100) return 'error';
    if (percentage === 100) return 'success';
    if (percentage >= 90) return 'warning';
    return 'primary';
  };

  // Calculate expense-specific totals for accurate progress tracking
  const expenseTotals = React.useMemo(() => {
    const actualExpenses = budget.categories
      .filter(cat => cat.type === 'expense')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);
    
    const totalIncome = budget.categories
      .filter(cat => cat.type === 'income')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);
    
    return {
      actual: actualExpenses,
      budgeted: totalIncome,
      remaining: totalIncome - actualExpenses
    };
  }, [budget.categories]);

  // Calculate overall budget health (income vs total expenses)
  const overallBudget = React.useMemo(() => {
    const totalIncome = budget.categories
      .filter(cat => cat.type === 'income')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);
    
    const totalExpenses = budget.categories
      .filter(cat => cat.type === 'expense')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);
    
    return {
      income: totalIncome,
      expenses: totalExpenses,
      netRemaining: totalIncome - totalExpenses
    };
  }, [budget.categories]);

  const handleEditCategory = (category: BudgetCategory, index: number) => {
    setEditingCategory({
      index,
      name: category.name,
      budgetedAmount: category.budgetedAmount.toString(),
      type: category.type
    });
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setEditingMonth(null);
  };

  const handleSaveCategory = async () => {
    if (!editingCategory) return;

    const newAmount = parseFloat(editingCategory.budgetedAmount);
    if (isNaN(newAmount) || newAmount < 0 || !editingCategory.name.trim()) return;

    const updatedCategories = budget.categories.map((cat, i) => 
      i === editingCategory.index
        ? {
            ...cat,
            name: editingCategory.name.trim(),
            budgetedAmount: editingCategory.type === 'income' ? cat.actualAmount : newAmount,
            type: editingCategory.type
          }
        : cat
    );

    const totalBudgeted = updatedCategories
      .filter(cat => cat.type === 'expense' || cat.type === 'savings')
      .reduce((sum, cat) => sum + cat.budgetedAmount, 0);

    try {
      await budgets.update(budget._id, {
        categories: updatedCategories,
        totalBudgeted
      });
      onUpdate({ categories: updatedCategories, totalBudgeted });
      setEditingCategory(null);
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleSaveMonth = async () => {
    if (!editingMonth) return;

    try {
      // Parse the YYYY-MM format and create a date for the first day of that month in local time
      const [year, month] = editingMonth.split('-').map(Number);
      const monthToSave = new Date(year, month - 1, 1).toISOString(); // month - 1 because JS months are 0-indexed
      console.log('Saving month update:', { budgetId: budget._id, editingMonth, monthToSave });
      
      await budgets.update(budget._id, {
        month: monthToSave
      });
      onUpdate({ month: monthToSave });
      setEditingMonth(null);
      showNotification('Budget month updated successfully', 'success');
    } catch (error) {
      console.error('Error updating month:', error);
      showNotification('Failed to update budget month. Please try again.', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await budgets.delete(budget._id);
      setIsDeleteDialogOpen(false);
      showNotification(`Budget for ${formatDate(budget.month)} has been deleted successfully`, 'success');
      
      // Refresh transactions to reflect the deleted budget's transactions
      await refreshTransactions();
      
      // Call onDelete callback to handle budget removal in parent component
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting budget:', error);
      showNotification('Failed to delete budget. Please try again.', 'error');
    }
  };

  const handleAddCategory = (type: 'income' | 'expense' | 'savings') => {
    setNewCategory({
      name: '',
      budgetedAmount: '0',
      type
    });
  };

  const handleSaveNewCategory = async () => {
    if (!newCategory || !newCategory.name.trim()) return;

    const newAmount = parseFloat(newCategory.budgetedAmount);
    if ((newCategory.type === 'expense' || newCategory.type === 'savings') && (isNaN(newAmount) || newAmount < 0)) return;

    const newCategoryObj: BudgetCategory = {
      name: newCategory.name.trim(),
      budgetedAmount: newCategory.type === 'income' ? 0 : newAmount,
      actualAmount: 0,
      type: newCategory.type
    };

    const updatedCategories = [...budget.categories, newCategoryObj];
    const totalBudgeted = updatedCategories
      .filter(cat => cat.type === 'expense' || cat.type === 'savings')
      .reduce((sum, cat) => sum + cat.budgetedAmount, 0);

    try {
      await budgets.update(budget._id, {
        categories: updatedCategories,
        totalBudgeted
      });
      onUpdate({ categories: updatedCategories, totalBudgeted });
      setNewCategory(null);
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;

    const updatedCategories = budget.categories.filter(
      cat => !(cat.name === deletingCategory.name && cat.type === deletingCategory.type)
    );

    const totalBudgeted = updatedCategories
      .filter(cat => cat.type === 'expense' || cat.type === 'savings')
      .reduce((sum, cat) => sum + cat.budgetedAmount, 0);

    try {
      await budgets.update(budget._id, {
        categories: updatedCategories,
        totalBudgeted
      });
      onUpdate({ categories: updatedCategories, totalBudgeted });
      
      // Refresh transactions to reflect deleted transactions
      await refreshTransactions();
      
      showNotification(`Category "${deletingCategory.name}" and all associated transactions have been deleted successfully`, 'success');
      setDeletingCategory(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification('Failed to delete category. Please try again.', 'error');
    }
  };

  const handleDeleteCategoryClick = (category: BudgetCategory) => {
    setDeletingCategory({
      name: category.name,
      type: category.type,
      actualAmount: category.actualAmount
    });
  };

  const handleCancelDelete = () => {
    setDeletingCategory(null);
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {editingMonth ? (
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                type="month"
                value={editingMonth}
                onChange={(e) => setEditingMonth(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <IconButton size="small" onClick={handleSaveMonth}>
                <CheckIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="h6" component="h2">
                {formatDate(budget.month)}
              </Typography>
              <IconButton size="small" onClick={() => {
                const date = new Date(budget.month);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                setEditingMonth(`${year}-${month}`);
              }}>
                <EditIcon />
              </IconButton>
            </Box>
          )}
          <Box>
            <Tooltip title="Delete Budget">
              <IconButton size="small" onClick={() => setIsDeleteDialogOpen(true)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="textSecondary">
            Net Remaining
          </Typography>
          <Typography 
            variant="body1" 
            color={overallBudget.netRemaining >= 0 ? 'success.main' : 'error.main'}
            sx={{ fontWeight: 'medium' }}
          >
            {formatCurrency(overallBudget.netRemaining)}
          </Typography>
        </Box>
        <Box width="100%" sx={{ position: 'relative' }}>

          <LinearProgress
            variant="determinate"
            value={expenseTotals.actual > expenseTotals.budgeted ? 100 : calculateProgress(expenseTotals.actual, expenseTotals.budgeted)}
            color={getProgressColor(expenseTotals.actual, expenseTotals.budgeted)}
            sx={{ 
              mb: 2,
            }}
          />
          <Box display="flex" justifyContent="flex-end" width="100%" mt={0.5}>
            <Typography variant="body2" color="textSecondary">
              Spending: {formatCurrency(expenseTotals.actual)} / {formatCurrency(expenseTotals.budgeted)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <List dense>
          <Typography variant="subtitle1" color="primary" sx={{ mt: 1, mb: 1 }}>
            Income
          </Typography>
          {budget.categories
            .filter(category => category.type === 'income')
            .map((category, _) => {
              const originalIndex = budget.categories.findIndex(cat => cat === category);
              return (
                <ListItem key={originalIndex} sx={{ px: 0 }}>
                  {editingCategory?.index === originalIndex ? (
                    <Box display="flex" flexDirection="column" width="100%" gap={1}>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          size="small"
                          value={editingCategory.name}
                          onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                          placeholder="Category Name"
                        />
                        <TextField
                          select
                          size="small"
                          value={editingCategory.type}
                          onChange={(e) => setEditingCategory({ ...editingCategory, type: e.target.value as 'income' | 'expense' | 'savings' })}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="income">Income</MenuItem>
                          <MenuItem value="expense">Expense</MenuItem>
                          <MenuItem value="savings">Savings</MenuItem>
                        </TextField>
                        <IconButton size="small" onClick={handleSaveCategory}>
                          <CheckIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={category.name}
                        secondary={`Income - ${formatCurrency(category.actualAmount)}`}
                      />
                      <Box display="flex" alignItems="center">
                        <IconButton size="small" onClick={() => handleEditCategory(category, originalIndex)} sx={{ ml: 1 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteCategoryClick(category)} sx={{ ml: 1 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </ListItem>
              );
            })}
          {newCategory?.type === 'income' ? (
            <ListItem sx={{ px: 0 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="Category Name"
                  autoFocus
                />
                <IconButton size="small" onClick={handleSaveNewCategory}>
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={() => setNewCategory(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </ListItem>
          ) : (
            <ListItemButton 
              onClick={() => handleAddCategory('income')}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5
              }}
            >
              <AddIcon fontSize="small" />
              <Typography variant="body2">Add Income Category</Typography>
            </ListItemButton>
          )}

          <Typography variant="subtitle1" color="success.main" sx={{ mt: 2, mb: 1 }}>
            Savings
          </Typography>
          {budget.categories
            .filter(category => category.type === 'savings')
            .map((category, _) => {
              const originalIndex = budget.categories.findIndex(cat => cat === category);
              return (
                <ListItem key={originalIndex} sx={{ px: 0 }}>
                  {editingCategory?.index === originalIndex ? (
                    <Box display="flex" flexDirection="column" width="100%" gap={1}>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          size="small"
                          value={editingCategory.name}
                          onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                          placeholder="Category Name"
                        />
                        {editingCategory.type === 'savings' && (
                          <TextField
                            size="small"
                            type="number"
                            value={editingCategory.budgetedAmount}
                            onChange={(e) => setEditingCategory({ ...editingCategory, budgetedAmount: e.target.value })}
                            placeholder="Amount"
                          />
                        )}
                        <TextField
                          select
                          size="small"
                          value={editingCategory.type}
                          onChange={(e) => setEditingCategory({ ...editingCategory, type: e.target.value as 'income' | 'expense' | 'savings' })}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="income">Income</MenuItem>
                          <MenuItem value="expense">Expense</MenuItem>
                          <MenuItem value="savings">Savings</MenuItem>
                        </TextField>
                        <IconButton size="small" onClick={handleSaveCategory}>
                          <CheckIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={category.name}
                        secondary={`Savings - ${formatCurrency(category.actualAmount)} of ${formatCurrency(category.budgetedAmount)}`}
                      />
                      <Box sx={{ width: '30%', ml: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            category.actualAmount > category.budgetedAmount
                              ? 100 
                              : Math.max(0, Math.min(calculateProgress(category.actualAmount, category.budgetedAmount, 'savings') + 100, 100))
                          }
                          color="primary"
                        />
                      </Box>
                      <Box display="flex" alignItems="center">
                        <IconButton size="small" onClick={() => handleEditCategory(category, originalIndex)} sx={{ ml: 1 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteCategoryClick(category)} sx={{ ml: 1 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </ListItem>
              );
            })}
          {newCategory?.type === 'savings' ? (
            <ListItem sx={{ px: 0 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="Category Name"
                  autoFocus
                />
                <TextField
                  size="small"
                  type="number"
                  value={newCategory.budgetedAmount}
                  onChange={(e) => setNewCategory({ ...newCategory, budgetedAmount: e.target.value })}
                  placeholder="Amount"
                />
                <IconButton size="small" onClick={handleSaveNewCategory}>
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={() => setNewCategory(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </ListItem>
          ) : (
            <ListItemButton 
              onClick={() => handleAddCategory('savings')}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5
              }}
            >
              <AddIcon fontSize="small" />
              <Typography variant="body2">Add Savings Category</Typography>
            </ListItemButton>
          )}

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 1 }}>
            Expenses
          </Typography>
          {budget.categories
            .filter(category => category.type === 'expense')
            .map((category, _) => {
              const originalIndex = budget.categories.findIndex(cat => cat === category);
              return (
                <ListItem key={originalIndex} sx={{ px: 0 }}>
                  {editingCategory?.index === originalIndex ? (
                    <Box display="flex" flexDirection="column" width="100%" gap={1}>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          size="small"
                          value={editingCategory.name}
                          onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                          placeholder="Category Name"
                        />
                        {(editingCategory.type === 'expense' || editingCategory.type === 'savings') && (
                          <TextField
                            size="small"
                            type="number"
                            value={editingCategory.budgetedAmount}
                            onChange={(e) => setEditingCategory({ ...editingCategory, budgetedAmount: e.target.value })}
                            placeholder="Amount"
                          />
                        )}
                        <TextField
                          select
                          size="small"
                          value={editingCategory.type}
                          onChange={(e) => setEditingCategory({ ...editingCategory, type: e.target.value as 'income' | 'expense' | 'savings' })}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="income">Income</MenuItem>
                          <MenuItem value="expense">Expense</MenuItem>
                          <MenuItem value="savings">Savings</MenuItem>
                        </TextField>
                        <IconButton size="small" onClick={handleSaveCategory}>
                          <CheckIcon />
                        </IconButton>
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={category.name}
                        secondary={`Expense - ${formatCurrency(category.actualAmount)} of ${formatCurrency(category.budgetedAmount)}`}
                      />
                      <Box sx={{ width: '30%', ml: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={category.actualAmount > category.budgetedAmount ? 100 : calculateProgress(category.actualAmount, category.budgetedAmount, 'expense')}
                          color={getProgressColor(category.actualAmount, category.budgetedAmount)}
                        />
                      </Box>
                      <Box display="flex" alignItems="center">
                        <IconButton size="small" onClick={() => handleEditCategory(category, originalIndex)} sx={{ ml: 1 }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteCategoryClick(category)} sx={{ ml: 1 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </ListItem>
              );
            })}
          {newCategory?.type === 'expense' ? (
            <ListItem sx={{ px: 0 }}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="Category Name"
                  autoFocus
                />
                <TextField
                  size="small"
                  type="number"
                  value={newCategory.budgetedAmount}
                  onChange={(e) => setNewCategory({ ...newCategory, budgetedAmount: e.target.value })}
                  placeholder="Amount"
                />
                <IconButton size="small" onClick={handleSaveNewCategory}>
                  <CheckIcon />
                </IconButton>
                <IconButton size="small" onClick={() => setNewCategory(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </ListItem>
          ) : (
            <ListItemButton 
              onClick={() => handleAddCategory('expense')}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5
              }}
            >
              <AddIcon fontSize="small" />
              <Typography variant="body2">Add Expense Category</Typography>
            </ListItemButton>
          )}
        </List>

        <Dialog
          open={!!deletingCategory}
          onClose={handleCancelDelete}
        >
          <DialogTitle>Delete Category</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the category "{deletingCategory?.name}"? This action cannot be undone.
              {deletingCategory && deletingCategory.actualAmount > 0 && (
                <>
                  <br /><br />
                  Warning: This category has transactions associated with it. Deleting the category will permanently delete all associated transactions.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button onClick={handleDeleteCategory} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
          <DialogTitle>Delete Budget</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this budget? This action cannot be undone.
            </Typography>
            <Typography color="warning.main" sx={{ mt: 2, fontWeight: 'medium' }}>
              ⚠️ Warning: Deleting this budget will also permanently delete all associated transactions.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default BudgetCard;